import type { NavigationGuardWithThis } from 'vue-router';
import type { paths } from '@/shared/api/autogen/schema';
import axios from 'axios';
import { authStore } from '@/app/providers/auth';
import { redirectToLogin } from '@/shared/lib/auth';
import { api } from '@/shared/api/client';

type AuthUserResponse = paths['/auth/user']['get']['responses']['200']['content']['application/json'];
type Role = AuthUserResponse['user']['role'];

const fetchAndSetUser = async () => {
    const response = await api.get('/auth/user');
    authStore.setUser(response.user);
};

const getRedirectRoute = (role: Role) => {
    // 配送スタッフは車両選択画面、それ以外は管理画面TOPに遷移
    if (role === 'at-sc') {
        return { name: 'VehicleSelect' };
    }
    return { name: 'admin-top' };
};

const getErrorRoute = (status: number) => {
    switch (status) {
        case 401:
            return null;
        case 403:
            return { name: 'error.forbidden' };
        default:
            return { name: 'error.serverError' };
    }
};

export const authGuard: NavigationGuardWithThis<undefined> = async (to) => {
    const { requiresAuth } = to.meta;
    const isAuthenticated = authStore.isAuthenticated.value;

    // cognitoからログインルートにリダイレクトされたらそのまま表示（AuthCallback.vue を実行）
    if (to.name === 'auth-callback') {
        return;
    }

    // ルートパスの場合はリダイレクト
    if (to.path === '/') {
        return getRedirectRoute(authStore.user.value?.role);
    }

    if (requiresAuth && !isAuthenticated) {
        await redirectToLogin();
        return;
    }

    if (!isAuthenticated) {
        return;
    }

    if (!requiresAuth) {
        return;
    }

    try {
        await fetchAndSetUser();

        // ロール制限のあるルートは、許可されていないロールを権限エラーに飛ばす
        // （APIも403を返すが、画面を開かせないことで表示崩れを防ぐ）
        const { allowedRoles } = to.meta;
        if (allowedRoles && !allowedRoles.includes(authStore.user.value?.role ?? '')) {
            return { name: 'error.forbidden' };
        }
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status ?? 500;
            const errorRoute = getErrorRoute(status);

            if (errorRoute) {
                return errorRoute;
            }

            authStore.clearAuth();
            await redirectToLogin();
            return;
        }

        return { name: 'error.serverError' };
    }
};
